import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Alert,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

export interface PaymentAttachmentUpload {
  fileName: string;
  contentType: string;
  base64: string;
}

export interface RegisterPaymentPayload {
  amount: number;
  paymentDate?: string;
  paymentMethod?: string;
  reference?: string;
  notes?: string;
  attachments?: PaymentAttachmentUpload[];
}

const MAX_ATTACHMENTS = 6;
const ALLOWED_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

interface RegisterPaymentDialogProps {
  open: boolean;
  title?: string;
  total: number;
  paidAmount: number;
  onClose: () => void;
  onConfirm: (payload: RegisterPaymentPayload) => Promise<void>;
}

const PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'Cheque', 'Tarjeta', 'Cuenta Corriente'];

const fmtCurrency = (n: number) =>
  `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);

const RegisterPaymentDialog: React.FC<RegisterPaymentDialogProps> = ({
  open,
  title,
  total,
  paidAmount,
  onClose,
  onConfirm,
}) => {
  const saldo = Math.max(0, (total || 0) - (paidAmount || 0));

  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [paymentDate, setPaymentDate] = useState(today());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<PaymentAttachmentUpload[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset the form whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setAmount(saldo > 0 ? String(saldo) : '');
      setPaymentMethod('Efectivo');
      setPaymentDate(today());
      setReference('');
      setNotes('');
      setAttachments([]);
      setError(null);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (files.length === 0) return;

    const invalid = files.find((f) => !ALLOWED_ATTACHMENT_TYPES.includes(f.type));
    if (invalid) {
      setError('Solo se permiten comprobantes en PDF o imagen (PNG, JPG, WEBP)');
      return;
    }

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setError(`Podés adjuntar hasta ${MAX_ATTACHMENTS} comprobantes`);
      return;
    }

    try {
      const uploads = await Promise.all(
        files.map(async (f) => ({
          fileName: f.name,
          contentType: f.type,
          base64: await readFileAsBase64(f),
        }))
      );
      setAttachments((prev) => [...prev, ...uploads]);
      setError(null);
    } catch {
      setError('No se pudo leer alguno de los archivos');
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    const value = Number(String(amount).replace(',', '.'));
    if (!value || value <= 0) {
      setError('Ingresá un monto mayor a cero');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onConfirm({
        amount: value,
        paymentDate,
        paymentMethod,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title || 'Registrar pago'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Typography variant="body2">Total: <b>{fmtCurrency(total)}</b></Typography>
          <Typography variant="body2" color="success.main">
            Abonado: <b>{fmtCurrency(paidAmount)}</b>
          </Typography>
          <Typography variant="body2" color={saldo > 0.5 ? 'warning.main' : 'success.main'} fontWeight={600}>
            Saldo: <b>{fmtCurrency(saldo)}</b>
          </Typography>
        </Stack>
        <Divider />
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="Monto del pago"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(',', '.'))}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
            autoFocus
            sx={{ minWidth: 180 }}
          />
          <TextField
            select
            label="Método de pago"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Fecha"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
        </Stack>
        <TextField
          label="Referencia (N° de transferencia, cheque, etc.)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          fullWidth
        />
        <TextField
          label="Notas"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <Divider />
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Comprobantes (PDF o imagen, hasta {MAX_ATTACHMENTS})
          </Typography>
          <Button
            component="label"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            disabled={attachments.length >= MAX_ATTACHMENTS}
            sx={{ alignSelf: 'flex-start' }}
          >
            Adjuntar comprobantes
            <input
              type="file"
              hidden
              multiple
              accept="application/pdf,image/*"
              onChange={handleFilesSelected}
            />
          </Button>
          {attachments.length > 0 && (
            <List dense disablePadding>
              {attachments.map((att, idx) => (
                <ListItem
                  key={`${att.fileName}-${idx}`}
                  disableGutters
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => removeAttachment(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={att.fileName} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving ? 'Guardando…' : 'Registrar pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterPaymentDialog;
